import axios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';

import * as SafeBase64 from '@utils/base64';

import { Authorization } from './auth';
import { ApiError, FallbackError } from './errors';
import {
    CaptureContext,
    CaptureExceptionSignature,
    HttpMethod,
    RequestContext,
    RequestPayload,
} from './types';

export class ApiClientClass {
    private defaultHeaders: AxiosRequestHeaders = {
        Accept: 'application/json',
        Platform: 'Web',
        'Platform-Image-Formats': 'avif,jpeg,png,svg,webp',
    };
    private unthrottledToken: string | null = null;
    private sentryHandler: CaptureExceptionSignature | null = null;
    private backendEndpoint: string | null = null;
    private invalidTokenHandler: () => void = () => {};

    constructor(private readonly auth: Authorization) {}

    public setSentryHandler(handler: CaptureExceptionSignature): void {
        this.sentryHandler = handler;
    }

    public setBackendEndpoint(endpoint: string): void {
        this.backendEndpoint = endpoint;
    }

    public setInvalidTokenHandler(handler: () => void): void {
        this.invalidTokenHandler = handler;
    }

    public setUnthrottledToken(token: string): void {
        this.unthrottledToken = token;
    }

    public setCustomHeaders(headers: Record<string, string>): void {
        this.defaultHeaders = {
            ...this.defaultHeaders,
            ...headers,
        };
    }

    public get<Response, Request extends RequestPayload = null>(
        url: string,
        payload?: Request,
    ): Promise<Response> {
        return this.request<Response>(HttpMethod.Get, url, payload);
    }

    public post<Response, Request extends RequestPayload = null>(
        url: string,
        payload?: Request,
    ): Promise<Response> {
        return this.request<Response>(HttpMethod.Post, url, payload, {
            'Content-Type': 'application/json',
        });
    }

    private request<T>(
        method: HttpMethod,
        rawPath: string,
        payload: RequestPayload,
        headers = {},
    ): Promise<T> {
        const path = rawPath.endsWith('/') ? rawPath : `${rawPath}/`;
        const requestHeaders: Record<string, string> = {
            ...this.defaultHeaders,
            ...headers,
            ...this.auth.getAuthHeaders(),
        };

        if (this.unthrottledToken !== null) {
            requestHeaders['X-Unthrottled-Authorization'] = `Bearer ${this.unthrottledToken}`;
        }

        const fetchPayload: AxiosRequestConfig = {
            headers: requestHeaders,
            withCredentials: true,
            data: payload,
            method,
        };

        if (payload !== null && payload !== undefined) {
            if (method === HttpMethod.Post) fetchPayload.data = payload;
            else if (method === HttpMethod.Get && Object.keys(payload || {}).length > 0) {
                fetchPayload.data = undefined;
                fetchPayload.params = { _body: SafeBase64.encode(JSON.stringify(payload)) };
            }
        }

        const url = this.backendEndpoint !== null ? `${this.backendEndpoint}${path}` : path;

        const request: RequestContext = { url, method, payload, headers: requestHeaders };

        return axios(url, fetchPayload)
            .then((response) => {
                const contentType = response.headers['content-type'];
                if (!contentType || contentType.indexOf('application/json') < 0) {
                    this.captureExceptionAndThrow(
                        FallbackError(request.url, request.method, response.status),
                        {
                            extra: { request, response: response.data },
                        },
                    );
                }

                return response.data as T;
            })
            .catch((error) => {
                if (error.response) {
                    const captureContext: CaptureContext = {
                        extra: { request, response: error.response },
                    };
                    const status = error.response.status;

                    if (status === 422) {
                        const ServerError = new ApiError(
                            'Backend sanitize error',
                            request.url,
                            request.method,
                            status,
                            error.response.data,
                        );

                        if (ServerError.getCommonCodes().includes('invalid_token')) {
                            this.invalidTokenHandler();
                        }

                        throw ServerError;
                    }

                    if (status === 404 && error.response.data?.error) {
                        // Backend сигналит "сущность не найдена" честным 404 — это ожидаемое
                        // состояние, не шлём в Sentry; BFF превратит в Next-овский notFound.
                        throw new ApiError(
                            'Not found',
                            request.url,
                            request.method,
                            status,
                            error.response.data,
                        );
                    }

                    this.captureExceptionAndThrow(
                        FallbackError(request.url, request.method, status),
                        captureContext,
                    );
                } else {
                    this.captureExceptionAndThrow(FallbackError(request.url, request.method, 500), {
                        extra: { request, message: error.message },
                    });
                }
            });
    }

    private captureException(error: ApiError, context: CaptureContext): void {
        if (this.sentryHandler) this.sentryHandler(error, context);
    }

    private captureExceptionAndThrow(error: ApiError, context: CaptureContext): never {
        this.captureException(error, context);
        throw error;
    }
}
